/**
 * Duplicate Submission Handler
 * Prevents duplicate contest submissions and provides intelligent feedback
 */

import { supabase } from '../lib/supabase';

class DuplicateSubmissionHandler {
  constructor() {
    this.submissionCache = new Map();
    this.rateLimitCache = new Map();
    this.RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5 minutes
    this.MAX_SUBMISSIONS_PER_WINDOW = 3;
  }

  /**
   * Check for duplicate submissions
   */
  async checkDuplicate(submissionData) {
    const checks = await Promise.allSettled([
      this.checkExactDuplicate(submissionData),
      this.checkSimilarContent(submissionData),
      this.checkUserSubmissionLimit(submissionData),
      this.checkRateLimit(submissionData)
    ]);

    const results = checks.map(result => 
      result.status === 'fulfilled' ? result.value : null
    ).filter(Boolean);

    return {
      isDuplicate: results.some(r => r.isDuplicate),
      duplicateType: results.find(r => r.isDuplicate)?.type || null,
      message: results.find(r => r.isDuplicate)?.message || null,
      existingSubmission: results.find(r => r.existingSubmission)?.existingSubmission || null,
      canSubmit: !results.some(r => r.isDuplicate),
      suggestions: results.filter(r => r.suggestion).map(r => r.suggestion)
    };
  }

  /**
   * Check for exact duplicate submissions
   */
  async checkExactDuplicate(submissionData) {
    try {
      const { email, title, description } = submissionData;
      
      // Check database first (production)
      if (supabase) {
        const { data: dbSubmissions } = await supabase
          .from('contest_submissions')
          .select('*')
          .eq('email', email)
          .eq('technique_title', title)
          .eq('technique_description', description);

        if (dbSubmissions?.length > 0) {
          return {
            isDuplicate: true,
            type: 'exact',
            message: 'You have already submitted this exact entry. Each participant can submit only one entry per contest.',
            existingSubmission: dbSubmissions[0]
          };
        }
      }

      // Check localStorage (local/fallback)
      const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
      const exactMatch = localSubmissions.find(sub => 
        sub.email === email && 
        sub.technique_title === title && 
        sub.technique_description === description
      );

      if (exactMatch) {
        return {
          isDuplicate: true,
          type: 'exact',
          message: 'You have already submitted this exact entry. Each participant can submit only one entry per contest.',
          existingSubmission: exactMatch
        };
      }

      return { isDuplicate: false };
    } catch (error) {
      console.warn('Exact duplicate check failed:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Check for similar content (fuzzy matching)
   */
  async checkSimilarContent(submissionData) {
    try {
      const { email, title, description } = submissionData;
      
      // Get user's previous submissions
      let userSubmissions = [];
      
      if (supabase) {
        const { data: dbSubmissions } = await supabase
          .from('contest_submissions')
          .select('*')
          .eq('email', email);
        
        if (dbSubmissions) userSubmissions = dbSubmissions;
      }

      // Also check localStorage
      const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
      const localUserSubmissions = localSubmissions.filter(sub => sub.email === email);
      
      // Combine and deduplicate
      const allSubmissions = [...userSubmissions, ...localUserSubmissions];
      const uniqueSubmissions = allSubmissions.filter((sub, index, arr) => 
        arr.findIndex(s => s.technique_title === sub.technique_title) === index
      );

      // Check for similar titles (>80% similarity)
      for (const existing of uniqueSubmissions) {
        const titleSimilarity = this.calculateSimilarity(title, existing.technique_title || existing.title);
        const descSimilarity = this.calculateSimilarity(description, existing.technique_description || existing.description);

        if (titleSimilarity > 0.8 || descSimilarity > 0.7) {
          return {
            isDuplicate: true,
            type: 'similar',
            message: `Your submission appears very similar to a previous entry: "${existing.technique_title || existing.title}". Please ensure your submission is unique.`,
            existingSubmission: existing,
            suggestion: 'Consider modifying your technique or providing more unique details to differentiate it.'
          };
        }
      }

      return { isDuplicate: false };
    } catch (error) {
      console.warn('Similar content check failed:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Check user submission limit per contest
   */
  async checkUserSubmissionLimit(submissionData) {
    try {
      const { email, contest_title } = submissionData;
      const maxSubmissionsPerContest = 1; // One submission per contest per user
      
      let userContestSubmissions = [];
      
      if (supabase) {
        const { data: dbSubmissions } = await supabase
          .from('contest_submissions')
          .select('*')
          .eq('email', email)
          .eq('contest_title', contest_title);
        
        if (dbSubmissions) userContestSubmissions = dbSubmissions;
      }

      // Also check localStorage
      const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
      const localContestSubmissions = localSubmissions.filter(sub => 
        sub.email === email && sub.contest_title === contest_title
      );
      
      const totalSubmissions = userContestSubmissions.length + localContestSubmissions.length;

      if (totalSubmissions >= maxSubmissionsPerContest) {
        return {
          isDuplicate: true,
          type: 'limit',
          message: `You have already submitted an entry for this contest. Only one submission per participant is allowed.`,
          existingSubmission: userContestSubmissions[0] || localContestSubmissions[0],
          suggestion: 'You can edit your existing submission or wait for the next contest.'
        };
      }

      return { isDuplicate: false };
    } catch (error) {
      console.warn('Submission limit check failed:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Check rate limiting (prevent spam)
   */
  checkRateLimit(submissionData) {
    try {
      const { email } = submissionData;
      const now = Date.now();
      const userKey = `rate_${email}`;
      
      // Get user's recent submissions
      const userRateData = this.rateLimitCache.get(userKey) || { submissions: [], lastCleanup: now };
      
      // Clean old submissions (outside window)
      userRateData.submissions = userRateData.submissions.filter(
        timestamp => now - timestamp < this.RATE_LIMIT_WINDOW
      );
      
      // Check if user exceeded rate limit
      if (userRateData.submissions.length >= this.MAX_SUBMISSIONS_PER_WINDOW) {
        const oldestSubmission = Math.min(...userRateData.submissions);
        const waitTime = Math.ceil((this.RATE_LIMIT_WINDOW - (now - oldestSubmission)) / 1000 / 60);
        
        return {
          isDuplicate: true,
          type: 'rate_limit',
          message: `Too many submission attempts. Please wait ${waitTime} minutes before trying again.`,
          suggestion: 'Take time to review and refine your submission before resubmitting.'
        };
      }

      // Add current attempt
      userRateData.submissions.push(now);
      this.rateLimitCache.set(userKey, userRateData);

      return { isDuplicate: false };
    } catch (error) {
      console.warn('Rate limit check failed:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Calculate text similarity using Levenshtein distance
   */
  calculateSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 1;
    
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 1;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * Record successful submission (for rate limiting)
   */
  recordSubmission(email) {
    const now = Date.now();
    const userKey = `submitted_${email}`;
    
    // Store in localStorage for persistence
    const submissionHistory = JSON.parse(localStorage.getItem('submission_history') || '{}');
    if (!submissionHistory[email]) {
      submissionHistory[email] = [];
    }
    
    submissionHistory[email].push(now);
    
    // Keep only recent submissions (last 24 hours)
    const dayAgo = now - (24 * 60 * 60 * 1000);
    submissionHistory[email] = submissionHistory[email].filter(timestamp => timestamp > dayAgo);
    
    localStorage.setItem('submission_history', JSON.stringify(submissionHistory));
  }

  /**
   * Get user's submission history
   */
  getUserSubmissionHistory(email) {
    try {
      const submissionHistory = JSON.parse(localStorage.getItem('submission_history') || '{}');
      return submissionHistory[email] || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Clear rate limit cache (for testing)
   */
  clearCache() {
    this.rateLimitCache.clear();
    this.submissionCache.clear();
  }

  /**
   * Get duplicate check summary for debugging
   */
  async getDuplicateCheckSummary(submissionData) {
    const checks = await Promise.allSettled([
      this.checkExactDuplicate(submissionData),
      this.checkSimilarContent(submissionData),
      this.checkUserSubmissionLimit(submissionData),
      this.checkRateLimit(submissionData)
    ]);

    return {
      exactDuplicate: checks[0].status === 'fulfilled' ? checks[0].value : { error: checks[0].reason },
      similarContent: checks[1].status === 'fulfilled' ? checks[1].value : { error: checks[1].reason },
      submissionLimit: checks[2].status === 'fulfilled' ? checks[2].value : { error: checks[2].reason },
      rateLimit: checks[3].status === 'fulfilled' ? checks[3].value : { error: checks[3].reason }
    };
  }
}

// Create singleton instance
const duplicateSubmissionHandler = new DuplicateSubmissionHandler();

export default duplicateSubmissionHandler;